import { useRef, useState } from "react";
import { useDialogA11y } from "../../hooks/useDialogA11y";
import { api } from "../../lib/api";
import { getAuthToken } from "../../lib/auth";

type Props = {
  targetType: "message" | "user";
  targetId: string;
  targetLabel?: string;
  onClose: () => void;
};

const REASONS = [
  { value: "spam",                 label: "Spam" },
  { value: "harassment",          label: "Harassment or bullying" },
  { value: "inappropriate_content", label: "Inappropriate content" },
  { value: "hate_speech",         label: "Hate speech" },
  { value: "impersonation",       label: "Impersonation" },
  { value: "other",               label: "Other" },
];

export default function ReportModal({ targetType, targetId, targetLabel, onClose }: Props) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogA11y(true, dialogRef, onClose);
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  async function submit() {
    if (!reason) { setErr("Please select a reason."); return; }
    setBusy(true);
    setErr("");
    try {
      await api.postAuth("/reports", { targetType, targetId, reason, details }, getAuthToken() || "");
      setDone(true);
    } catch (e: any) {
      setErr(e?.message || "Failed to submit report. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4" onClick={onClose}>
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-label={done ? "Report submitted" : "Report"} tabIndex={-1} className="w-full max-w-sm rounded-2xl border border-neutral-700 bg-neutral-900 p-6 shadow-2xl outline-none" onClick={e => e.stopPropagation()}>
        {done ? (
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/15 border border-emerald-500/30">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-7 w-7 text-emerald-400"><path d="M20 6 9 17l-5-5"/></svg>
              </span>
            </div>
            <h2 className="text-lg font-medium text-white">Report submitted</h2>
            <p className="text-sm text-fg-2">Thank you for helping keep Echo safe. We'll review this report.</p>
            <button onClick={onClose} className="w-full py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium transition-colors">
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h2 className="text-base font-medium text-white">
                  Report {targetType === "message" ? "message" : "user"}
                </h2>
                {targetLabel && (
                  <p className="text-xs text-fg-2 mt-0.5 truncate max-w-[220px]">
                    {targetLabel}
                  </p>
                )}
              </div>
              <button onClick={onClose} className="shrink-0 text-fg-3 hover:text-white transition-colors" aria-label="Close">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            {err && <p className="mb-3 text-sm text-red-400">{err}</p>}

            <div className="space-y-2 mb-4">
              <p className="text-xs text-fg-2">Reason</p>
              {REASONS.map(r => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setReason(r.value)}
                  className={`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-colors border ${
                    reason === r.value
                      ? "border-emerald-500/50 bg-emerald-500/10 text-white"
                      : "border-neutral-800 bg-neutral-800/40 text-fg-2 hover:border-neutral-700 hover:bg-neutral-800"
                  }`}
                >
                  <span className={`h-3.5 w-3.5 rounded-full border-2 shrink-0 flex items-center justify-center ${reason === r.value ? "border-emerald-400" : "border-neutral-600"}`}>
                    {reason === r.value && <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />}
                  </span>
                  {r.label}
                </button>
              ))}
            </div>

            <div className="mb-5">
              <p className="text-xs text-fg-2 mb-2">Additional details <span className="normal-case text-fg-3">(optional)</span></p>
              <textarea
                value={details}
                onChange={e => setDetails(e.target.value)}
                placeholder="Describe the issue..."
                aria-label="Additional details"
                maxLength={1000}
                rows={3}
                className="w-full rounded-lg bg-neutral-800 border border-neutral-700 text-sm text-fg placeholder-neutral-500 px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-emerald-600/50"
              />
            </div>

            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="flex-1 py-2.5 rounded-lg border border-neutral-700 text-fg-2 hover:bg-neutral-800 text-sm transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={busy || !reason}
                className="flex-1 py-2.5 rounded-lg bg-red-600 hover:bg-red-500 text-white text-sm font-medium transition-colors disabled:opacity-50"
              >
                {busy ? "Submitting…" : "Submit report"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
